import { css } from "@emotion/css";
import { Button, Drawer, Tooltip } from "@octopusdeploy/design-system-components";
import { StringHelper, DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, ArgoCDApplicationSourceResource } from "@octopusdeploy/octopus-server-client";
import { Repository } from "@octopusdeploy/octopus-server-client";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useCorrelationId, type ArgoCDEventDispatcher, type ArgoCDGenerateAnnotationsEventDispatcher } from "~/analytics/Analytics";
import type { ConfiguredSource, ConfiguredSources } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/ConfiguredSource";
import { AnnotationsVerificationStatus } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/AnnotationsVerificationStatus";
import { AnnotationsView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/AnnotationsView";
import { HelmExpressionConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/HelmExpressionConfigurationView";
import { ScopingConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/ScopingConfigurationView";
import { SourceSelectionTable } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/SourceSelectionTable";
import { verifyAnnotations } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/createAnnotation";
import { getEnvironmentsForArgoCDApplications } from "~/areas/infrastructure/components/ArgoCDInstances/getEnvironmentsForArgoCDApplications";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { sharedStyles } from "./sharedStyles";
import { useArgoCDEventDispatcher } from "./useArgoCDEventDispatcher";
import { useArgoCDGenerateAnnotationsEventDispatcher } from "./useArgoCDGenerateAnnotationsEventDispatcher";
const styles = {
    mainContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[24],
        padding: space[16],
        paddingBottom: space[24],
    }),
    contentContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    selectContainer: css({
        width: "50%",
    }),
};
type CurrentMultiSourceTab = "Selection" | "ScopingConfiguration" | "HelmConfiguration" | "Copy";
type AnnotationVerificationResult = "Success" | "Failure" | "NotVerified";
type InnerActions = {
    reset: () => void;
};
type GenerateArgoCdAnnotationsDrawerInnerProps = {
    gatewayId?: string;
    initiallySelectedApplicationId?: string;
    initiallySelectedApplicationSourceIndex?: number;
    initiallySelectedProjectSlug?: string;
    currentTab: CurrentMultiSourceTab;
    onSelectedApplicationChanged: (app: ArgoCDApplicationResource) => void;
    onSelectedSourcesChanged: (selectedSources: ArgoCDApplicationSourceResource[]) => void;
    onConfiguredSourcesChanged: (configuredSources: ConfiguredSources) => void;
    isVerifyingAnnotations: boolean;
    annotationsVerificationResult: AnnotationVerificationResult;
    onAnnotationVerificationResult: (result: AnnotationVerificationResult) => void;
} & GenerateArgoCdAnnotationsDrawerInnerAnalyticProps;
type GenerateArgoCdAnnotationsDrawerInnerAnalyticProps = {
    argoCDEventDispatcher: ArgoCDEventDispatcher;
    argoCDGenerateAnnotationsEventDispatcher: ArgoCDGenerateAnnotationsEventDispatcher;
    baseAnalyticsEvent: {
        gatewayName?: string;
        spaceId?: string;
        correlationId: string;
        sourcePage: string;
    };
};
const appHasMultipleSources = (app: ArgoCDApplicationResource) => app.Sources.length > 1;
const appHasAnyHelmSources = (app: ArgoCDApplicationResource) => app.Sources.filter((s) => s.SourceType == "Helm").length > 0;
export const GenerateArgoCdAnnotationsDrawerInner = forwardRef<InnerActions, GenerateArgoCdAnnotationsDrawerInnerProps>((props, ref) => {
    const { currentTab, onSelectedApplicationChanged, onSelectedSourcesChanged, onConfiguredSourcesChanged, gatewayId, initiallySelectedProjectSlug, initiallySelectedApplicationId, initiallySelectedApplicationSourceIndex, argoCDEventDispatcher, argoCDGenerateAnnotationsEventDispatcher, baseAnalyticsEvent, isVerifyingAnnotations, annotationsVerificationResult, onAnnotationVerificationResult, } = props;
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const [selectedApplication, setSelectedApplication] = useState<ArgoCDApplicationResource | undefined>(undefined);
    const [selectedSources, setSelectedSources] = useState<ArgoCDApplicationSourceResource[]>([]);
    const [configuredSources, setConfiguredSources] = useState<ConfiguredSources>({});
    const { result: applications, isLoading, error, } = useInlineStatusQuery(async (repo) => {
        if (gatewayId) {
            const response = await repo.ArgoCDGateways.listApps(gatewayId);
            return response.Resources;
        }
        else {
            const instances = await repo.ArgoCDInstances.list();
            const applicationsResponsePromises = instances.Resources.map((instance) => repo.ArgoCDGateways.listApps(instance.GatewayId, { take: Repository.takeAll }));
            const applicationResponses = await Promise.all(applicationsResponsePromises);
            return applicationResponses.map((ar) => ar.Resources).reduce((accumulator, value) => accumulator.concat(value), []);
        }
    }, [gatewayId], "Listing Argo CD applications");
    const { result: targetData, isLoading: isLoadingTargetData, error: targetDataError, } = useInlineStatusQuery(async (repo) => {
        const projectsPromise = repo.Projects.all();
        const environmentsPromise = getEnvironmentsForArgoCDApplications(repo, ephemeralEnvironmentsEnabled);
        const tenantsPromise = repo.Tenants.all();
        return {
            projects: await projectsPromise,
            environments: (await environmentsPromise).Items,
            tenants: await tenantsPromise,
        };
    }, [ephemeralEnvironmentsEnabled], "List projects, environments and tenants for annotations drawer");
    useInlineStatusQuery(async (repo) => {
        if (!isVerifyingAnnotations || !selectedApplication) {
            return;
        }
        try {
            const updatedApp = await repo.ArgoCDGateways.getApp(selectedApplication.ArgoCDGatewayId, selectedApplication.Id);
            const annotationVerificationResult = verifyAnnotations(updatedApp.Resource, configuredSources);
            onAnnotationVerificationResult(annotationVerificationResult ? "Success" : "Failure");
        }
        catch (e) {
            onAnnotationVerificationResult("Failure");
        }
    }, [configuredSources, isVerifyingAnnotations, onAnnotationVerificationResult, selectedApplication], "Verifying Annotations");
    useEffect(() => {
        if (isLoading || isLoadingTargetData || !applications || !targetData) {
            return;
        }
        argoCDGenerateAnnotationsEventDispatcher("Click Argo CD Generate Annotations", {
            ...baseAnalyticsEvent,
            availableApplications: applications.length,
            availableProjects: targetData.projects.length,
            availableTenants: targetData.tenants.length,
            availableEnvironments: targetData.environments.length,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isLoadingTargetData]);
    const findApplication = useCallback((id: string | undefined) => applications?.find((app) => app.Id === id), [applications]);
    const isMultiSourceApplication = selectedApplication ? appHasMultipleSources(selectedApplication) : false;
    const hasAnyHelmSources = selectedApplication ? appHasAnyHelmSources(selectedApplication) : false;
    const items = useMemo(() => (applications || []).map((app) => ({
        value: app.Id,
        text: app.Name,
    })), [applications]);
    const updateConfiguredSources = useCallback((configured: ConfiguredSources) => {
        setConfiguredSources(configured);
        onConfiguredSourcesChanged(configured);
    }, [onConfiguredSourcesChanged]);
    const presetConfiguredSources = useCallback((selectedApplication: ArgoCDApplicationResource | undefined, selectedSources: ArgoCDApplicationSourceResource[]) => {
        const newConfiguredSources = (selectedApplication?.Sources || []).reduce<Record<number, ConfiguredSource>>((dict, src) => {
            dict[src.Index] = {
                source: src,
                isSelectedForScoping: selectedSources.some((selectedSrc) => selectedSrc.Index === src.Index),
                isHelmSource: src.SourceType == "Helm",
                projectSlug: initiallySelectedProjectSlug,
            };
            return dict;
        }, {});
        updateConfiguredSources(newConfiguredSources);
    }, [initiallySelectedProjectSlug, updateConfiguredSources]);
    const updateSelectedSources = useCallback((selectedApplication: ArgoCDApplicationResource | undefined, selectedSources: ArgoCDApplicationSourceResource[]) => {
        setSelectedSources(selectedSources);
        onSelectedSourcesChanged(selectedSources);
        //preset the configured sources
        presetConfiguredSources(selectedApplication, selectedSources);
    }, [onSelectedSourcesChanged, presetConfiguredSources]);
    const updatedSelectedApplication = useCallback((id: string | undefined) => {
        const selectedApp = findApplication(id);
        setSelectedApplication(selectedApp);
        if (selectedApp) {
            onSelectedApplicationChanged(selectedApp);
        }
        //we've changed the application, so update the selected sources
        //if we have no application, or it's multiple sources, nothing is selected, otherwise, the single source is selected
        updateSelectedSources(selectedApp, !selectedApp || appHasMultipleSources(selectedApp) ? [] : selectedApp.Sources);
    }, [findApplication, onSelectedApplicationChanged, updateSelectedSources]);
    const selectApplication = useCallback((id: string | undefined) => {
        argoCDEventDispatcher("Select Argo CD Generate Annotations Drawer Application");
        updatedSelectedApplication(id);
    }, [argoCDEventDispatcher, updatedSelectedApplication]);
    useEffect(() => {
        //if we don't have a selected application, and we have a response with an application, preselect it
        if (!selectedApplication) {
            if (initiallySelectedApplicationId) {
                updatedSelectedApplication(initiallySelectedApplicationId);
                const selectedApp = findApplication(initiallySelectedApplicationId);
                if (selectedApp && initiallySelectedApplicationSourceIndex && selectedSources.length <= 0) {
                    updateSelectedSources(selectedApp, selectedApp.Sources?.filter((s) => s.Index === initiallySelectedApplicationSourceIndex) ?? []);
                }
            }
            else if (applications?.[0]) {
                updatedSelectedApplication(applications[0].Id);
            }
        }
    }, [applications, findApplication, initiallySelectedApplicationId, initiallySelectedApplicationSourceIndex, selectedApplication, selectedSources.length, updateSelectedSources, updatedSelectedApplication]);
    useImperativeHandle(ref, () => ({
        reset: () => {
            //set the selected application to undefined, which then should force the re-selection of the first item
            setSelectedApplication(undefined);
        },
    }));
    const content = useMemo(() => {
        const annotationVerificationCallout = () => {
            if (isVerifyingAnnotations) {
                return <AnnotationsVerificationStatus status={"waiting"}/>;
            }
            if (annotationsVerificationResult === "Success") {
                return <AnnotationsVerificationStatus status={"success"}/>;
            }
            if (annotationsVerificationResult === "Failure") {
                return <AnnotationsVerificationStatus status={"failure"}/>;
            }
            return <></>;
        };
        const applicationSelection = (<div>
                <h2 className={sharedStyles.header}>1. Select an application</h2>
                <div className={styles.contentContainer}>
                    <div className={styles.selectContainer}>
                        <DeprecatedSelect items={items} value={selectedApplication?.Id} onChange={selectApplication} label={"Application"} allowClear={false} allowFilter={true} disabled={isLoading}/>
                    </div>
                </div>
            </div>);
        if (isMultiSourceApplication) {
            switch (currentTab) {
                case "Selection":
                    return (<>
                            {applicationSelection}
                            <SourceSelectionTable application={selectedApplication} selectedSources={selectedSources} setSelectedSources={(srcs) => updateSelectedSources(selectedApplication, srcs)}/>
                        </>);
                case "ScopingConfiguration":
                    return (<ScopingConfigurationView index={3} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} projects={targetData?.projects} environments={targetData?.environments} tenants={targetData?.tenants} isMultiSourceApplication={isMultiSourceApplication} argoCDEventDispatcher={argoCDEventDispatcher}/>);
                case "HelmConfiguration":
                    return <HelmExpressionConfigurationView index={4} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} isMultiSourceApplication={isMultiSourceApplication}/>;
                case "Copy":
                    return (<>
                            {annotationVerificationCallout()}
                            <AnnotationsView index={4} configuredSources={configuredSources} application={selectedApplication}/>
                        </>);
            }
        }
        else {
            return (<>
                    {annotationVerificationCallout()}
                    {applicationSelection}
                    <ScopingConfigurationView index={2} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} projects={targetData?.projects} environments={targetData?.environments} tenants={targetData?.tenants} isMultiSourceApplication={isMultiSourceApplication} argoCDEventDispatcher={argoCDEventDispatcher}/>
                    {hasAnyHelmSources && <HelmExpressionConfigurationView index={3} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} isMultiSourceApplication={isMultiSourceApplication}/>}
                    <AnnotationsView index={3} configuredSources={configuredSources} application={selectedApplication}/>
                </>);
        }
    }, [
        items,
        selectedApplication,
        selectApplication,
        isLoading,
        isMultiSourceApplication,
        isVerifyingAnnotations,
        annotationsVerificationResult,
        currentTab,
        selectedSources,
        configuredSources,
        updateConfiguredSources,
        targetData?.projects,
        targetData?.environments,
        targetData?.tenants,
        argoCDEventDispatcher,
        updateSelectedSources,
        hasAnyHelmSources,
    ]);
    return <div className={styles.mainContainer}>{content}</div>;
});
type GenerateArgoCDAnnotationsDrawerProps = Pick<GenerateArgoCdAnnotationsDrawerInnerProps, "gatewayId" | "initiallySelectedApplicationId" | "initiallySelectedApplicationSourceIndex" | "initiallySelectedProjectSlug"> & GenerateArgoCdAnnotationsDrawerAnalyticProps & {
    open: boolean;
    onClose: () => void;
};
type GenerateArgoCdAnnotationsDrawerAnalyticProps = {
    gatewayName?: string;
    spaceId?: string;
    sourcePage: string;
};
export const GenerateArgoCDAnnotationsDrawer = (props: GenerateArgoCDAnnotationsDrawerProps) => {
    const { open, onClose, gatewayName, spaceId, sourcePage, ...rest } = props;
    const helmReplacementPathFromContainerReferenceFeature = useOctopusFeatureToggle("argo-cd-helm-replace-path-from-container-reference", false);
    const [currentTab, setCurrentTab] = useState<CurrentMultiSourceTab>("Selection");
    const [isMultiSourceApp, setIsMultiSourceApp] = useState<boolean>(false);
    const [hasAnyHelmSources, setHasAnyHelmSources] = useState<boolean>(false);
    const [hasAnySelectedSources, setHasAnySelectedSources] = useState<boolean>(false);
    const [hasValidSelectedSources, setHasValidSelectedSources] = useState<boolean>(false);
    const [allConfiguredSourcesValid, setAllConfiguredSourcesValid] = useState<boolean>(false);
    const [isVerifyingAnnotations, setIsVerifyingAnnotations] = useState<boolean>(false);
    const [annotationsVerificationResult, setAnnotationsVerificationResult] = useState<AnnotationVerificationResult>("NotVerified");
    const innerRef = useRef<InnerActions>(null);
    const correlationId = useCorrelationId();
    const baseAnalyticsEvent = { gatewayName, spaceId, correlationId, sourcePage };
    const argoCDEventDispatcher = useArgoCDEventDispatcher(gatewayName, spaceId, correlationId);
    const argoCDGenerateAnnotationsEventDispatcher = useArgoCDGenerateAnnotationsEventDispatcher();
    const handleSelectedApplicationChanged = (app: ArgoCDApplicationResource) => {
        setIsMultiSourceApp(appHasMultipleSources(app));
        setHasAnyHelmSources(appHasAnyHelmSources(app));
    };
    const handleSelectedSourcesChanged = (selectedSources: ArgoCDApplicationSourceResource[]) => {
        setHasAnySelectedSources(selectedSources.length > 0);
        setHasValidSelectedSources(selectedSources.every((src) => !StringHelper.isNullOrWhiteSpace(src.Name)));
    };
    const handleConfiguredSourcesChanged = (configuredSources: ConfiguredSources) => {
        setAllConfiguredSourcesValid(Object.entries(configuredSources)
            .filter(([, configuredSource]) => configuredSource.isSelectedForScoping)
            .every(
        //selected configured sources require project & environment slugs.
        ([, configuredSource]) => !StringHelper.isNullOrWhiteSpace(configuredSource.projectSlug || null) && !StringHelper.isNullOrWhiteSpace(configuredSource.environmentSlug || null)));
        setAnnotationsVerificationResult("NotVerified");
    };
    const resetToStart = () => {
        setCurrentTab("Selection");
        innerRef.current?.reset();
    };
    const handleOnClose = useCallback(() => {
        argoCDEventDispatcher("Close Argo CD Generate Annotations Drawer");
        onClose();
        setCurrentTab("Selection");
        setIsMultiSourceApp(false);
        setIsVerifyingAnnotations(false);
        setAnnotationsVerificationResult("NotVerified");
    }, [onClose, argoCDEventDispatcher]);
    const handleAnnotationVerification = () => {
        setIsVerifyingAnnotations(true);
    };
    const onAnnotationVerificationResult = (result: AnnotationVerificationResult) => {
        setIsVerifyingAnnotations(false);
        setAnnotationsVerificationResult(result);
    };
    const multiSourceTabSequence: CurrentMultiSourceTab[] = useMemo(() => (hasAnyHelmSources && !helmReplacementPathFromContainerReferenceFeature ? ["Selection", "ScopingConfiguration", "HelmConfiguration", "Copy"] : ["Selection", "ScopingConfiguration", "Copy"]), [hasAnyHelmSources, helmReplacementPathFromContainerReferenceFeature]);
    const moveToNextTab = useCallback(() => {
        const idx = multiSourceTabSequence.indexOf(currentTab);
        if (idx === multiSourceTabSequence.length) {
            throw new Error("Cannot move past the end of the list of tabs");
        }
        setCurrentTab(multiSourceTabSequence[idx + 1]);
    }, [currentTab, multiSourceTabSequence]);
    const moveToPreviousTab = useCallback(() => {
        const idx = multiSourceTabSequence.indexOf(currentTab);
        if (idx === 0) {
            throw new Error("Cannot move past the start of the list of tabs");
        }
        setCurrentTab(multiSourceTabSequence[idx - 1]);
    }, [currentTab, multiSourceTabSequence]);
    const customActions = useMemo(() => {
        // Hide the close button when the annotations are successfully verified
        const closeButton = annotationsVerificationResult === "Success" ? <></> : <Button importance="quiet" onClick={handleOnClose} label="Close"/>;
        const primaryButton = () => {
            if (!isVerifyingAnnotations && annotationsVerificationResult === "NotVerified") {
                return <Button importance="primary" onClick={handleAnnotationVerification} label={"Verify Annotations"}/>;
            }
            if (isVerifyingAnnotations) {
                return <Button importance="primary" onClick={handleAnnotationVerification} label={"Verifying Annotations"} disabled/>;
            }
            if (!isVerifyingAnnotations && annotationsVerificationResult === "Failure") {
                return <Button importance="primary" onClick={handleAnnotationVerification} label="Retry"/>;
            }
            return <Button importance="primary" onClick={handleOnClose} label="Done"/>;
        };
        if (isMultiSourceApp) {
            switch (currentTab) {
                case "Selection":
                    let nextFromSelectionButton = <Button importance="primary" onClick={moveToNextTab} label="Next" disabled={!hasValidSelectedSources || !hasAnySelectedSources}/>;
                    if (!hasAnySelectedSources) {
                        nextFromSelectionButton = <Tooltip content={"Must select at least one source"}>{nextFromSelectionButton}</Tooltip>;
                    }
                    else if (!hasValidSelectedSources) {
                        nextFromSelectionButton = <Tooltip content={"All selected sources must be named"}>{nextFromSelectionButton}</Tooltip>;
                    }
                    return [closeButton, nextFromSelectionButton];
                case "ScopingConfiguration":
                    let nextFromScopingButton = <Button importance="primary" onClick={moveToNextTab} label="Next" disabled={!allConfiguredSourcesValid}/>;
                    if (!allConfiguredSourcesValid) {
                        nextFromScopingButton = <Tooltip content={"All sources must have required scopes specified"}>{nextFromScopingButton}</Tooltip>;
                    }
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, nextFromScopingButton];
                case "HelmConfiguration":
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, <Button importance="primary" onClick={moveToNextTab} label="Next"/>];
                case "Copy":
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, <Button importance="secondary" onClick={resetToStart} label="Add another application"/>, primaryButton()];
            }
        }
        else {
            return [closeButton, primaryButton()];
        }
    }, [handleOnClose, isMultiSourceApp, isVerifyingAnnotations, annotationsVerificationResult, currentTab, moveToNextTab, hasValidSelectedSources, hasAnySelectedSources, allConfiguredSourcesValid, moveToPreviousTab]);
    return (<Drawer variant={"customActions"} isOpen={open} onClose={handleOnClose} title={"Generate annotations"} width={"wide"} layout={"basic"} customActions={customActions}>
            <GenerateArgoCdAnnotationsDrawerInner ref={innerRef} {...rest} currentTab={currentTab} onSelectedApplicationChanged={handleSelectedApplicationChanged} onSelectedSourcesChanged={handleSelectedSourcesChanged} onConfiguredSourcesChanged={handleConfiguredSourcesChanged} argoCDEventDispatcher={argoCDEventDispatcher} argoCDGenerateAnnotationsEventDispatcher={argoCDGenerateAnnotationsEventDispatcher} baseAnalyticsEvent={baseAnalyticsEvent} isVerifyingAnnotations={isVerifyingAnnotations} annotationsVerificationResult={annotationsVerificationResult} onAnnotationVerificationResult={onAnnotationVerificationResult}/>
        </Drawer>);
};
